/* eslint-disable @typescript-eslint/no-explicit-any */
import { capitalize } from "@mui/material";
import { createContext, ReactNode, useContext } from "react";

type Entry = Readonly<[string, any]>; // Define the type of each entry as a tuple
type ConvertEntriesToObject<T extends readonly Entry[]> = {
  [K in T[number][0]]: Extract<T[number], readonly [K, any]>[1];
};
/**
 * Creates a context with react hooks.
 * @example
 * const foo = createStatefulContext("foo", (options: { bar: string }) => {
 *   const [state, setState] = useState(0);
 *   return {
 *     state,
 *     setState,
 *   };
 * });
 *
 * export const { WithFooContext, fooContext, useFooContext } = foo;
 */
export const createStatefulContext = <
  N extends string,
  F extends (...args: any) => any,
>(
  name: N,
  factory: F
) => {
  const Context = createContext<ReturnType<F> | undefined>(undefined);
  /**
   * Injects the context.
   */
  const Wrapper = (
    props: Parameters<F>[0] extends Record<string, any>
      ? Parameters<F>[0] & {
          children: ReactNode;
        }
      : {
          children: ReactNode;
        }
  ) => {
    const { Provider } = Context;
    const value = factory(props);
    return <Provider value={value as any}>{props.children}</Provider>;
  };
  /**
   * Uses the context.
   */
  const useHook = () => {
    const context = useContext(Context);
    if (!context) {
      throw new Error("could not find context");
    }
    return context as ReturnType<F>;
  };
  const capitalizedName = capitalize(name) as Capitalize<N>;
  const entries = [
    [`${name}Context`, Context],
    [`With${capitalizedName}Context`, Wrapper],
    [`use${capitalizedName}Context`, useHook],
    [`use${capitalizedName}ContextFactory`, factory],
  ] as const;
  return Object.fromEntries(entries) as ConvertEntriesToObject<typeof entries>;
};
